import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Container, Card, Row, Col, Badge, Button, Spinner, Alert, Image, Table, Collapse, Modal, Form } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { getItemPermissions, canToggleEditLock } from '../utils/permissionUtils';
import { API_HOST_BASE } from '../utils/apiUtils';
import { useNotification } from '../hooks/useNotification';
import EvaluationReportModal from './EvaluationReportModal';
import './ReportModule.css';

const ReportView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [report, setReport] = useState(null);
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openSections, setOpenSections] = useState({
    reportInfo: true,
    projectInfo: false
  });
  
  // Approval workflow state
  const [showDecisionModal, setShowDecisionModal] = useState(false);
  const [decision, setDecision] = useState('');
  const [remarks, setRemarks] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [editLockLoading, setEditLockLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [docxLoading, setDocxLoading] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const { notification, showSuccess, showError, dismiss } = useNotification();

  // Toggle edit lock function
  const handleToggleEditLock = async () => {
    if (!report) return;
    setEditLockLoading(true);
    try {
      const newLockState = !report.edit_locked;
      await axios.put(`/reports/${report.report_id}/edit-lock`, { edit_locked: newLockState });
      setReport(prev => ({ ...prev, edit_locked: newLockState }));
      setSuccessMessage(`Edit permissions ${newLockState ? 'locked' : 'unlocked'} successfully.`);
      setShowSuccessModal(true);
    } catch (err) {
      console.error('Failed to toggle edit lock:', err);
      showError('Failed to toggle edit lock');
    } finally {
      setEditLockLoading(false);
    }
  };

  useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`/reports/${id}`);
        const data = res.data;
        console.log('ReportView - Report data received:', data);
        console.log('ReportView - Report status:', data.status);
        setReport(data);
        
        // Fetch project data if available
        if (data.project_id) {
          try {
            const projectRes = await axios.get(`/projects/${data.project_id}`);
            setProject(projectRes.data);
          } catch (projectErr) {
            console.log('Could not load project data:', projectErr);
          }
        }
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id, navigate]);

  const statusVariant = (status) => {
    switch (status) {
      case 'Draft': return 'secondary';
      case 'Pending': return 'warning';
      case 'Approved by VC-RDES': return 'success';
      case 'Approved by Chancellor': return 'success';
      case 'Rejected by VC-RDES': return 'danger';
      case 'Rejected by Chancellor': return 'danger';
      default: return 'secondary';
    }
  };

  const getStatusVariant = (status) => {
    switch (status) {
      case 'Completed':
        return 'success';
      case 'Ongoing':
        return 'primary';
      case 'Not Started':
        return 'secondary';
      case 'Cancelled':
        return 'danger';
      case 'Pending':
        return 'warning';
      case 'Approved by VC-RDES':
        return 'success';
      case 'Approved by Chancellor':
        return 'success';
      case 'Rejected by VC-RDES':
        return 'danger';
      case 'Rejected by Chancellor':
        return 'danger';
      default:
        return 'secondary';
    }
  };

  // Approval workflow functions
  const canCurrentUserDecide = (report) => {
    if (!user || !report) return false;
    const role = user.role;
    const status = report.status;
    
    if (role === 'Vice Chancellor for Research, Development and Extension Services') {
      return status === 'Pending';
    }
    if (role === 'Chancellor') {
      return status === 'Approved by VC-RDES';
    }
    if (role === 'Admin') {
      return ['Pending', 'Approved by VC-RDES'].includes(status);
    }
    return false;
  };

  const getNextApprovalStep = (report) => {
    if (!report) return null;
    const status = report.status;
    
    if (status === 'Pending') {
      return 'To be reviewed by VC-RDES';
    }
    if (status === 'Approved by VC-RDES') {
      return 'To be approved by Chancellor';
    }
    return null;
  };

  const getApprovalChecklist = (report) => {
    if (!report) return [];
    const status = report.status;
    
    // Only show checks for approved statuses, not rejected ones
    const vcrdesDone = ['Approved by VC-RDES', 'Approved by Chancellor'].includes(status);
    const chancellorDone = ['Approved by Chancellor'].includes(status);
    
    return [
      {
        key: 'vcrdes',
        label: 'Reviewed by VC-RDES',
        done: vcrdesDone,
        rejected: status === 'Rejected by VC-RDES'
      },
      {
        key: 'chancellor',
        label: 'Approved by Chancellor',
        done: chancellorDone,
        rejected: status === 'Rejected by Chancellor'
      }
    ];
  };

  const handleDecisionClick = (decisionType) => {
    setDecision(decisionType);
    setRemarks(''); // Reset remarks when opening modal
    setShowDecisionModal(true);
  };

  const submitDecision = async () => {
    if (!decision) return;
    
    // Remarks are now optional for both approval and rejection
    setSubmitting(true);
    try {
      const response = await axios.post(`/reports/${id}/decision`, {
        decision,
        remarks: remarks || null
      });
      
      // Update the report status and remarks
      setReport(prev => ({
        ...prev,
        status: response.data.status,
        remarks: remarks || null
      }));
      
      // Show success message
      if (decision === 'Approved') {
        showSuccess('Evaluation report approved successfully!');
      } else if (decision === 'Rejected') {
        showSuccess('Evaluation report rejected successfully!');
      }
      
      setShowDecisionModal(false);
      setDecision('');
      setRemarks('');
    } catch (err) {
      console.error('Error submitting decision:', err);
      const errorMessage = err.response?.data?.error || 'Failed to submit decision';
      setError(errorMessage);
      showError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  // Handle DOCX download for Evaluation Report
  const handleDownloadDocx = async () => {
    try {
      setError('');
      setDocxLoading(true);
      
      // Generate the evaluation report DOCX
      const response = await axios.post('/reports/generate-docx', {
        report_id: report.report_id
      }, {
        responseType: 'blob' // Important for file download
      });
      
      // Create blob URL and trigger download
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = `Evaluation_Report_${report.report_id}.docx`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/"/g, '');
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      showSuccess('Evaluation Report DOCX downloaded successfully!');
    } catch (error) {
      console.error('Error generating DOCX report:', error);
      const errorMessage = error.response?.data?.error || 'Failed to generate DOCX report. Please try again.';
      setError(errorMessage);
      showError(errorMessage);
    } finally {
      setDocxLoading(false);
    }
  };

  const asList = (str) => {
    try {
      return str ? str.split(',').map(s => s.trim()).filter(s => s) : [];
    } catch { 
      return []; 
    }
  };

  // Normalize JSON-like fields to clean, human-readable text
  const normalizeListish = (value) => {
    if (value === null || value === undefined) return '—';
    if (Array.isArray(value)) return value.join(', ');
    if (typeof value === 'object') {
      const vals = Object.values(value).filter(v => v !== undefined && v !== null && String(v).trim() !== '');
      if (vals.length) return vals.map(v => String(v)).join(', ');
      const keys = Object.keys(value);
      return keys.length ? keys.join(', ') : '—';
    }
    const str = String(value).trim();
    if (!str) return '—';
    try {
      if ((str.startsWith('[') && str.endsWith(']')) || (str.startsWith('{') && str.endsWith('}'))) {
        const parsed = JSON.parse(str);
        if (Array.isArray(parsed)) return parsed.join(', ');
        if (parsed && typeof parsed === 'object') {
          const vals = Object.values(parsed);
          return vals.length ? vals.join(', ') : Object.keys(parsed).join(', ');
        }
      }
    } catch {}
    const cleaned = str.replace(/[\[\]\{\}"]/g, '');
    const parts = cleaned.split(/[,;]+/).map(s => s.trim()).filter(Boolean);
    return parts.length ? parts.join(', ') : cleaned;
  };

  const formatPeso = (value) => {
    if (value === null || value === undefined) return '—';
    if (typeof value === 'number' && Number.isFinite(value)) return `₱${value.toLocaleString()}`;
    const str = String(value).trim();
    if (!str) return '—';
    const numeric = str.replace(/[^0-9.\-]/g, '');
    const num = parseFloat(numeric);
    if (!Number.isFinite(num)) return str; // fall back to raw string if not parseable
    return `₱${num.toLocaleString()}`;
  };

  const toggleSection = (section) => {
    setOpenSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const renderMonitoringEvaluationTable = (monitoringData) => {
    if (!monitoringData) return <div>—</div>;

    // 1) Try ProjectView style: expect an object with rows: []
    try {
      const parsed = typeof monitoringData === 'string' ? JSON.parse(monitoringData) : monitoringData;
      if (parsed && Array.isArray(parsed.rows)) {
        const rows = parsed.rows;
        return (
          <div className="table-responsive">
            <table className="table table-bordered table-sm me-view-table" style={{ tableLayout: 'fixed', width: '100%' }}>
              <thead className="table-primary">
                <tr>
                  <th style={{ width: '8%', fontSize: '0.9rem', padding: '12px 8px' }}>Objectives</th>
                  <th style={{ width: '12%', fontSize: '0.9rem', padding: '12px 8px' }}>Performance Indicators</th>
                  <th style={{ width: '10%', fontSize: '0.9rem', padding: '12px 8px' }}>Baseline Data</th>
                  <th style={{ width: '10%', fontSize: '0.9rem', padding: '12px 8px' }}>Performance Target</th>
                  <th style={{ width: '10%', fontSize: '0.9rem', padding: '12px 8px' }}>Data Source</th>
                  <th style={{ width: '10%', fontSize: '0.9rem', padding: '12px 8px' }}>Collection Method</th>
                  <th style={{ width: '10%', fontSize: '0.9rem', padding: '12px 8px' }}>Frequency of Data Collection</th>
                  <th style={{ width: '18%', fontSize: '0.9rem', padding: '12px 8px' }}>Office/Persons Responsible</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, idx) => (
                  <tr key={`mev-${idx}`} style={{ verticalAlign: 'top' }}>
                    <td style={{ padding: '8px', fontSize: '0.85rem', whiteSpace: 'pre-wrap' }}>{r.objectives || '—'}</td>
                    <td style={{ padding: '8px', fontSize: '0.85rem', whiteSpace: 'pre-wrap' }}>{r.performance_indicators || '—'}</td>
                    <td style={{ padding: '8px', fontSize: '0.85rem', whiteSpace: 'pre-wrap' }}>{r.baseline_data || '—'}</td>
                    <td style={{ padding: '8px', fontSize: '0.85rem', whiteSpace: 'pre-wrap' }}>{r.performance_target || '—'}</td>
                    <td style={{ padding: '8px', fontSize: '0.85rem', whiteSpace: 'pre-wrap' }}>{r.data_source || '—'}</td>
                    <td style={{ padding: '8px', fontSize: '0.85rem', whiteSpace: 'pre-wrap' }}>{r.collection_method || '—'}</td>
                    <td style={{ padding: '8px', fontSize: '0.85rem', whiteSpace: 'pre-wrap' }}>{r.frequency || '—'}</td>
                    <td style={{ padding: '8px', fontSize: '0.85rem', whiteSpace: 'pre-wrap' }}>{r.responsible || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      }
    } catch {}

    // 2) Fallback to older object-with-sections format
    try {
      const tableData = typeof monitoringData === 'string' ? JSON.parse(monitoringData) : monitoringData;
      if (!tableData || typeof tableData !== 'object') return <div>—</div>;
      const objectiveTypes = [
        { key: 'impact', label: 'Impact-' },
        { key: 'outcome', label: 'Outcome-' },
        { key: 'output', label: 'Output-' },
        { key: 'activities', label: 'Activities-' },
        { key: 'input', label: 'Input-' }
      ];
      return (
        <div className="table-responsive">
          <table className="table table-bordered me-view-table" style={{ fontSize: '0.85rem' }}>
            <thead className="table-light">
              <tr>
                <th style={{ width: '8%', padding: '8px', textAlign: 'center' }}>Objectives</th>
                <th style={{ width: '12%', padding: '8px' }}>Performance Indicators</th>
                <th style={{ width: '10%', padding: '8px' }}>Baseline Data</th>
                <th style={{ width: '10%', padding: '8px' }}>Performance Target</th>
                <th style={{ width: '10%', padding: '8px' }}>Data Source</th>
                <th style={{ width: '10%', padding: '8px' }}>Collection Method</th>
                <th style={{ width: '10%', padding: '8px' }}>Frequency of Data Collection</th>
                <th style={{ width: '18%', padding: '8px' }}>Office/Persons Responsible</th>
              </tr>
            </thead>
            <tbody>
              {objectiveTypes.map(({ key, label }) => {
                const rowData = tableData[key] || {};
                return (
                  <tr key={key} style={{ verticalAlign: 'top' }}>
                    <td className="fw-bold bg-light" style={{ padding: '12px 8px', fontSize: '0.9rem', textAlign: 'center' }}>{label}</td>
                    <td style={{ padding: '8px', fontSize: '0.85rem' }}>{rowData.performance_indicators || '—'}</td>
                    <td style={{ padding: '8px', fontSize: '0.85rem' }}>{rowData.baseline_data || '—'}</td>
                    <td style={{ padding: '8px', fontSize: '0.85rem' }}>{rowData.performance_target || '—'}</td>
                    <td style={{ padding: '8px', fontSize: '0.85rem' }}>{rowData.data_source || '—'}</td>
                    <td style={{ padding: '8px', fontSize: '0.85rem' }}>{rowData.collection_method || '—'}</td>
                    <td style={{ padding: '8px', fontSize: '0.85rem' }}>{rowData.frequency || '—'}</td>
                    <td style={{ padding: '8px', fontSize: '0.85rem' }}>{rowData.responsible ?? '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      );
    } catch {
      // 3) Final fallback - show raw text
      return <div style={{ whiteSpace: 'pre-wrap' }}>{String(monitoringData)}</div>;
    }
  };

  if (loading) {
    return (
      <div className="container-fluid px-3 px-md-4 py-3">
        <div className="text-center py-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p className="mt-3">Loading report details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container-fluid px-3 px-md-4 py-3">
        <Alert variant="danger">
          <Alert.Heading>Error</Alert.Heading>
          <p>{error}</p>
          <hr />
          <div className="d-flex justify-content-end">
            <Button variant="outline-danger" onClick={() => navigate('/reports')}>
              Back to Reports
            </Button>
          </div>
        </Alert>
      </div>
    );
  }

  if (!report) return null;

  return (
    <div className="container-fluid px-3 px-md-4 py-3">
        {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h2 className="mb-1">Evaluation Report Details</h2>
            <p className="text-muted mb-0">Project: {report.project_title}</p>
          </div>
          <div className="d-flex gap-2">
            {/* Decision Buttons for authorized users */}
            {user && (user.role === 'Vice Chancellor for Research, Development and Extension Services' || 
                      user.role === 'Chancellor' || 
                      user.role === 'Admin') && canCurrentUserDecide(report) && (
              <>
                <Button 
                  size="sm" 
                  variant="success" 
                  style={{ minWidth: '40px', height: '32px' }} 
                  onClick={() => handleDecisionClick('Approved')}
                  disabled={submitting}
                  title="Approve"
                >
                  <i className="bi bi-check-lg"></i>
                </Button>
                <Button 
                  size="sm" 
                  variant="danger" 
                  style={{ minWidth: '40px', height: '32px' }} 
                  onClick={() => handleDecisionClick('Rejected')}
                  disabled={submitting}
                  title="Reject"
                >
                  <i className="bi bi-x-lg"></i>
                </Button>
              </>
            )}
            {/* Edit Button - using new permission system */}
            {user && report && (() => {
              const permissions = getItemPermissions(user, report, 'report');
              return permissions.canEdit;
            })() && (
              <Button 
                size="sm" 
                variant="outline-primary" 
                style={{ minWidth: '40px', height: '32px' }} 
                onClick={() => setShowEditModal(true)}
                title="Edit evaluation report"
              >
                <i className="bi bi-pencil"></i>
              </Button>
            )}

            {/* Lock/Unlock Edit Permission button - for elevated roles */}
            {user && report && canToggleEditLock(user) && (
              <Button 
                variant={report.edit_locked ? "warning" : "outline-secondary"}
                size="sm"
                style={{ minWidth: '40px', height: '32px' }}
                onClick={handleToggleEditLock}
                disabled={editLockLoading}
                title={report.edit_locked ? "Unlock Edit Permission" : "Lock Edit Permission"}
              >
                <i className={`bi ${report.edit_locked ? 'bi-lock-fill' : 'bi-unlock'}`}></i>
              </Button>
            )}
            <Button 
              variant="outline-primary"
              size="sm"
              style={{ minWidth: '40px', height: '32px' }}
              onClick={handleDownloadDocx}
              disabled={docxLoading}
              title="Download as Word document (DOCX)"
            >
              {docxLoading ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <i className="bi bi-file-earmark-word"></i>
              )}
            </Button>
            <Button 
              variant="secondary"
              size="sm"
              style={{ minWidth: '40px', height: '32px' }}
              onClick={() => navigate('/reports')}
              title="Back"
            >
              <i className="bi bi-arrow-left"></i>
            </Button>
          </div>
        </div>


        {/* Report Information */}
        <Card className="shadow-sm mb-4">
          <Card.Header 
            className="bg-success text-white" 
            style={{ cursor: 'pointer' }}
            onClick={() => toggleSection('reportInfo')}
          >
            <div className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">
                <i className="bi bi-clipboard-data me-2"></i>
                Report Information
              </h5>
              <i className={`bi bi-chevron-${openSections.reportInfo ? 'up' : 'down'}`}></i>
            </div>
          </Card.Header>
          <Collapse in={openSections.reportInfo}>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <div className="mb-3">
                    <strong>Report Created By:</strong>
                    <div>{report.created_by_name}</div>
                    <small className="text-muted">{report.created_by_department}</small>
                  </div>
                  <div className="mb-3">
                    <strong>Report Date:</strong>
                    <div>{new Date(report.created_at).toLocaleDateString()}</div>
                  </div>
                  <div className="mb-3">
                    <strong>Participant Type:</strong>
                    <div>{report.participant_type || '—'}</div>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="mb-3">
                    <strong>Male BatStateU Participants:</strong>
                    <div>{report.male_batstateu_participants || 0}</div>
                  </div>
                  <div className="mb-3">
                    <strong>Female BatStateU Participants:</strong>
                    <div>{report.female_batstateu_participants || 0}</div>
                  </div>
                  <div className="mb-3">
                    <strong>Male Other Participants:</strong>
                    <div>{report.male_other_participants || 0}</div>
                  </div>
                  <div className="mb-3">
                    <strong>Female Other Participants:</strong>
                    <div>{report.female_other_participants || 0}</div>
                  </div>
                </Col>
              </Row>

              {/* Status and Approval Workflow */}
              <Row className="mb-3">
                <Col md={6}>
                  <div className="mb-3">
                    <strong>Status:</strong> <Badge bg={statusVariant(report.status || 'Pending')}>{report.status || 'Pending'}</Badge>
                    {!report.status && (
                      <small className="text-muted d-block">Status not set, defaulting to Pending</small>
                    )}
                    <div className="mt-1"><strong>Created:</strong> {new Date(report.created_at).toLocaleString()}</div>
                    
                    {/* Remarks for approved or rejected status */}
                    {report.remarks && (
                      <div className="mt-3">
                        {(['Rejected by VC-RDES', 'Rejected by Chancellor'].includes(report.status)) ? (
                          <div className="alert alert-info border-0" style={{ backgroundColor: '#f8f9fa', borderLeft: '4px solid #dc3545' }}>
                            <div className="d-flex align-items-start">
                              <i className="bi bi-exclamation-triangle-fill text-danger me-2 mt-1" style={{ fontSize: '1.2rem' }}></i>
                              <div className="w-100">
                                <strong className="text-danger d-block">
                                  <i className="bi bi-exclamation-circle-fill me-1"></i>Remarks:
                                </strong>
                                <div className="mt-2" style={{ whiteSpace: 'pre-wrap' }}>{report.remarks}</div>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="alert alert-info border-0" style={{ backgroundColor: '#f8f9fa', borderLeft: '4px solid #28a745' }}>
                            <div className="d-flex align-items-start">
                              <i className="bi bi-check-circle-fill text-success me-2 mt-1" style={{ fontSize: '1.2rem' }}></i>
                              <div className="w-100">
                                <strong className="text-success d-block">
                                  <i className="bi bi-info-circle-fill me-1"></i>Remarks:
                                </strong>
                                <div className="mt-2" style={{ whiteSpace: 'pre-wrap' }}>{report.remarks}</div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Approval Workflow under Status - following Project View format */}
                    <div className="mt-2">
                      {(() => {
                        const next = getNextApprovalStep(report);
                        return next ? (
                          <div className="text-muted small mt-1 d-flex align-items-center" style={{ fontStyle: 'italic' }}>
                            <i className="bi bi-eye me-1" aria-hidden="true"></i>
                            <span>{next}</span>
                          </div>
                        ) : null;
                      })()}
                      
                      {/* Approval Checklist */}
                      <div className="mt-2">
                        {getApprovalChecklist(report).map(step => (
                          <div key={step.key} className="d-flex align-items-center small">
                            <i className={`bi ${step.done ? 'bi-check-square-fill text-success' : 'bi-square text-muted' } me-1`} aria-hidden="true"></i>
                            <span className="text-muted">{step.label}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </Col>
              </Row>



              {report.narrative_of_activity && (
                <Row>
                  <Col>
                    <div className="mb-3">
                      <strong>Narrative of Activity:</strong>
                      <div className="mt-2 p-3 bg-light rounded">
                        {report.narrative_of_activity}
                      </div>
                    </div>
                  </Col>
                </Row>
              )}


              {/* Activity Rating Table */}
              <Row>
                <Col>
                  <div className="mb-3">
                    <strong>Activity Rating:</strong>
                    <div className="mt-2">
                      <Table responsive bordered size="sm">
                        <thead className="table-light">
                          <tr>
                            <th>Rating</th>
                            <th>BatStateU</th>
                            <th>Other Institution</th>
                            <th>Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td><strong>Excellent</strong></td>
                            <td>{report.activity_excellent_batstateu || 0}</td>
                            <td>{report.activity_excellent_other || 0}</td>
                            <td>{(report.activity_excellent_batstateu || 0) + (report.activity_excellent_other || 0)}</td>
                          </tr>
                          <tr>
                            <td><strong>Very Satisfactory</strong></td>
                            <td>{report.activity_very_satisfactory_batstateu || 0}</td>
                            <td>{report.activity_very_satisfactory_other || 0}</td>
                            <td>{(report.activity_very_satisfactory_batstateu || 0) + (report.activity_very_satisfactory_other || 0)}</td>
                          </tr>
                          <tr>
                            <td><strong>Satisfactory</strong></td>
                            <td>{report.activity_satisfactory_batstateu || 0}</td>
                            <td>{report.activity_satisfactory_other || 0}</td>
                            <td>{(report.activity_satisfactory_batstateu || 0) + (report.activity_satisfactory_other || 0)}</td>
                          </tr>
                          <tr>
                            <td><strong>Fair</strong></td>
                            <td>{report.activity_fair_batstateu || 0}</td>
                            <td>{report.activity_fair_other || 0}</td>
                            <td>{(report.activity_fair_batstateu || 0) + (report.activity_fair_other || 0)}</td>
                          </tr>
                          <tr>
                            <td><strong>Poor</strong></td>
                            <td>{report.activity_poor_batstateu || 0}</td>
                            <td>{report.activity_poor_other || 0}</td>
                            <td>{(report.activity_poor_batstateu || 0) + (report.activity_poor_other || 0)}</td>
                          </tr>
                        </tbody>
                      </Table>
                    </div>
                  </div>
                </Col>
              </Row>

              {/* Timeliness Rating Table */}
              <Row>
                <Col>
                  <div className="mb-3">
                    <strong>Timeliness Rating:</strong>
                    <div className="mt-2">
                      <Table responsive bordered size="sm">
                        <thead className="table-light">
                          <tr>
                            <th>Rating</th>
                            <th>BatStateU</th>
                            <th>Other Institution</th>
                            <th>Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td><strong>Excellent</strong></td>
                            <td>{report.timeliness_excellent_batstateu || 0}</td>
                            <td>{report.timeliness_excellent_other || 0}</td>
                            <td>{(report.timeliness_excellent_batstateu || 0) + (report.timeliness_excellent_other || 0)}</td>
                          </tr>
                          <tr>
                            <td><strong>Very Satisfactory</strong></td>
                            <td>{report.timeliness_very_satisfactory_batstateu || 0}</td>
                            <td>{report.timeliness_very_satisfactory_other || 0}</td>
                            <td>{(report.timeliness_very_satisfactory_batstateu || 0) + (report.timeliness_very_satisfactory_other || 0)}</td>
                          </tr>
                          <tr>
                            <td><strong>Satisfactory</strong></td>
                            <td>{report.timeliness_satisfactory_batstateu || 0}</td>
                            <td>{report.timeliness_satisfactory_other || 0}</td>
                            <td>{(report.timeliness_satisfactory_batstateu || 0) + (report.timeliness_satisfactory_other || 0)}</td>
                          </tr>
                          <tr>
                            <td><strong>Fair</strong></td>
                            <td>{report.timeliness_fair_batstateu || 0}</td>
                            <td>{report.timeliness_fair_other || 0}</td>
                            <td>{(report.timeliness_fair_batstateu || 0) + (report.timeliness_fair_other || 0)}</td>
                          </tr>
                          <tr>
                            <td><strong>Poor</strong></td>
                            <td>{report.timeliness_poor_batstateu || 0}</td>
                            <td>{report.timeliness_poor_other || 0}</td>
                            <td>{(report.timeliness_poor_batstateu || 0) + (report.timeliness_poor_other || 0)}</td>
                          </tr>
                        </tbody>
                      </Table>
                    </div>
                  </div>
                </Col>
              </Row>

              {/* Photos - Row-based Display */}
              {report.photos && report.photos.length > 0 && (() => {
                // Check if photos are in row-based structure
                const isRowBased = report.photos[0]?.row_id !== undefined || report.photos[0]?.frameCount !== undefined;
                const photoRows = isRowBased ? report.photos : [];
                
                // If not row-based, convert to rows for display
                const displayRows = isRowBased ? photoRows : (() => {
                  const rows = [];
                  for (let i = 0; i < report.photos.length; i += 2) {
                    rows.push({
                      row_id: `row_${i}`,
                      frameCount: Math.min(2, report.photos.length - i),
                      photos: report.photos.slice(i, i + 2),
                      caption: ''
                    });
                  }
                  return rows;
                })();
                
                return (
                  <Row>
                    <Col>
                      <div className="mb-3">
                        <strong>Activity Photos:</strong>
                        <div className="mt-2">
                          {displayRows.map((row, rowIndex) => (
                            <div key={row.row_id || rowIndex} className="mb-4">
                              <Row>
                                {row.photos && Array.isArray(row.photos) && row.photos.length > 0 && row.photos.map((photo, photoIndex) => {
                                  if (!photo || (!photo.url && !photo.filename)) {
                                    console.warn('Invalid photo data:', photo);
                                    return null;
                                  }
                                  
                                  const photoUrl = (() => {
                                    let url = photo.url;
                                    if (!url && photo.filename) {
                                      url = `/uploads/report-photos/${photo.filename}`;
                                    }
                                    if (url && !url.startsWith('http')) {
                                      url = `${API_HOST_BASE}${url.startsWith('/') ? '' : '/'}${url}`;
                                    }
                                    if (process.env.NODE_ENV !== 'production') {
                                      console.log(`Photo URL for row ${rowIndex + 1}, photo ${photoIndex + 1}:`, url);
                                    }
                                    return url;
                                  })();
                                  
                                  return (
                                    <Col md={row.frameCount === 1 ? 12 : 6} key={photo.photo_id || `photo-${rowIndex}-${photoIndex}`} className="mb-3">
                                      <div className="position-relative">
                                        <Image
                                          src={photoUrl}
                                          alt={`Row ${rowIndex + 1} Photo ${photoIndex + 1}`}
                                          fluid
                                          className="rounded shadow-sm"
                                          style={{ width: '100%', height: '200px', objectFit: 'cover' }}
                                          onError={(e) => {
                                            console.error('Photo failed to load:', e.target.src);
                                            console.error('Photo data:', photo);
                                            e.target.style.display = 'none';
                                          }}
                                          onLoad={() => {
                                            if (process.env.NODE_ENV !== 'production') {
                                              console.log('Photo loaded successfully:', photoUrl);
                                            }
                                          }}
                                        />
                                        {/* Per-Photo Caption - displayed directly under each photo */}
                                        {row.captionType === 'photo' && photo.caption && (
                                          <div className="mt-2 text-center">
                                            <small className="text-dark fst-italic">{photo.caption}</small>
                                          </div>
                                        )}
                                      </div>
                                    </Col>
                                  );
                                })}
                              </Row>
                              {/* Row Caption - displayed below the entire row */}
                              {row.captionType === 'row' && row.caption && (
                                <div className="mt-2 text-center">
                                  <small className="text-dark fst-italic">{row.caption}</small>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    </Col>
                  </Row>
                );
              })()}

            </Card.Body>
          </Collapse>
        </Card>

        {/* Project Information */}
        {project && (
          <Card className="shadow-sm mb-4">
            <Card.Header 
              className="bg-info text-white" 
              style={{ cursor: 'pointer' }}
              onClick={() => toggleSection('projectInfo')}
            >
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">
                  <i className="bi bi-folder me-2"></i>
                  Project Information
                </h5>
                <i className={`bi bi-chevron-${openSections.projectInfo ? 'up' : 'down'}`}></i>
              </div>
            </Card.Header>
            <Collapse in={openSections.projectInfo}>
              <Card.Body>
                <Row className="mb-3">
                  <Col md={6}><strong>Coordinator:</strong> {project.coordinator_fullname || project.coordinator_name || '—'}</Col>
                  <Col md={3}><strong>Status:</strong> <Badge bg={getStatusVariant(project.status)}>{project.status}</Badge></Col>
                  <Col md={3}><strong>Submitted:</strong> {project.date_submitted ? new Date(project.date_submitted).toLocaleString() : '—'}</Col>
                </Row>
                <Row>
                  <Col md={6}><strong>Request Type:</strong> {project.request_type || '—'}</Col>
                  <Col md={6}><strong>Event Type:</strong> {project.event_type || '—'}</Col>
                </Row>
                <Row className="mt-2">
                  <Col md={6}><strong>Location:</strong> {project.location || '—'}</Col>
                  <Col md={6}>
                    <strong>Duration:</strong> 
                    {project.start_date ? (
                      <div>
                        <div>
                <strong>Start:</strong> {project.start_date ? new Date(project.start_date).toLocaleDateString() : '—'}
                {project.start_time && ` at ${project.start_time}`}
              </div>
              {project.end_date && (
                <div>
                  <strong>End:</strong> {new Date(project.end_date).toLocaleDateString()}
                  {project.end_time && ` at ${project.end_time}`}
                          </div>
                        )}
                      </div>
                    ) : (
                      '—'
                    )}
                  </Col>
                </Row>
                
                {/* Remarks section - only show for approved or rejected projects */}
                {(project.status === 'Approved' || project.status === 'Rejected') && project.remarks && (
                  <Row className="mt-3">
                    <Col md={12}>
                      <div className="alert alert-info border-0" style={{ backgroundColor: '#f8f9fa', borderLeft: '4px solid #dc3545' }}>
                        <div className="d-flex align-items-start">
                          <i className="bi bi-exclamation-triangle-fill text-danger me-2 mt-1" style={{ fontSize: '1.2rem' }}></i>
                          <div>
                            <strong className="text-danger">
                              <i className="bi bi-exclamation-circle-fill me-1"></i>Remarks:
                            </strong>
                            <div className="mt-2" style={{ whiteSpace: 'pre-wrap' }}>{project.remarks}</div>
                          </div>
                        </div>
                      </div>
                    </Col>
                  </Row>
                )}
                
                <hr />
                <Row>
                  <Col md={6}><strong>Extension Agenda:</strong><div>{normalizeListish(project.extension_agenda)}</div></Col>
                  <Col md={6}><strong>SDG Goals:</strong><div>{normalizeListish(project.sdg_goals)}</div></Col>
                </Row>
                <hr />
                {/* Project leadership section */}
                <div className="mb-3">
                  <strong>Project Leader:</strong>
                  <div className="mt-1">{project.project_leader_name || project.project_leader || '—'}</div>
                </div>
                <div className="mb-3">
                  <strong>Assistant Project Leaders:</strong>
                  <div className="mt-1">{project.assistant_project_leader_names ? 
                    (() => {
                      try {
                        const names = typeof project.assistant_project_leader_names === 'string' && project.assistant_project_leader_names.trim().startsWith('[')
                          ? JSON.parse(project.assistant_project_leader_names)
                          : project.assistant_project_leader_names;
                        return Array.isArray(names) ? names.join(', ') : names;
                      } catch {
                        return project.assistant_project_leader_names;
                      }
                    })() : (project.assistant_project_leaders || '—')}</div>
                </div>
                <div className="mb-3">
                  <strong>Coordinators:</strong>
                  <div className="mt-1">{project.coordinator_names ? 
                    (() => {
                      try {
                        const names = typeof project.coordinator_names === 'string' && project.coordinator_names.trim().startsWith('[')
                          ? JSON.parse(project.coordinator_names)
                          : project.coordinator_names;
                        return Array.isArray(names) ? names.join(', ') : names;
                      } catch {
                        return project.coordinator_names;
                      }
                    })() : (project.coordinators || '—')}</div>
                </div>
                <hr />
                {/* Offices and Programs */}
                {[
                  ['offices_involved','Office/s / College/s / Organization/s Involved'],
                  ['programs_involved','Program/s Involved'],
                  ['partner_agencies','Partner Agencies'],
                  ['beneficiaries','Beneficiaries'],
                  ['total_cost','Total Cost']
                ].map(([key, label]) => (
                  <div className="mb-3" key={key}>
                    <strong>{label}:</strong>
                    <div className="mt-1" style={{ whiteSpace: 'pre-wrap' }}>
                      {key === 'total_cost' ? formatPeso(project[key]) : (project[key] || '—')}
                    </div>
                  </div>
                ))}

                {/* Fund Source placed after Total Cost (as in proposal) */}
                <div className="mb-3">
                  <strong>Fund Source:</strong>
                  <div className="mt-1">{normalizeListish(project.fund_source)}</div>
                </div>
                
                {/* Narrative Sections (Financial Plan rendered separately as table) */}
                {[ 
                  ['rationale','Rationale'],
                  ['objectives','Objectives (General and Specific)'],
                  ['expected_output','Expected Output'],
                  ['strategies_methods','Description, Strategies and Methods'],
                  ['functional_relationships','Functional Relationships']
                ].map(([key, label]) => (
                  <div className="mb-3" key={key}>
                    <strong>{label}:</strong>
                    <div className="mt-1" style={{ whiteSpace: 'pre-wrap' }}>{project[key] || '—'}</div>
                  </div>
                ))}

                {/* Financial Plan Table */}
                <div className="mb-3">
                  <strong>Financial Plan:</strong>
                  <div className="mt-2">
                    {(() => {
                      const raw = project.financial_plan_details;
                      if (!raw) return <div>—</div>;
                      let rows;
                      try { rows = typeof raw === 'string' ? JSON.parse(raw) : raw; } catch { rows = null; }
                      if (!Array.isArray(rows) || rows.length === 0) {
                        return <div style={{ whiteSpace: 'pre-wrap' }}>{normalizeListish(raw)}</div>;
                      }
                      const currency = (v) => {
                        const n = parseFloat(v);
                        return Number.isFinite(n) ? n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : (v || '');
                      };
                      const grand = rows.reduce((sum, r) => sum + (parseFloat(r.total) || 0), 0);
                      return (
                        <div className="table-responsive" style={{ border: '1px solid #dee2e6' }}>
                          <table className="table table-bordered table-sm mb-0" style={{ borderCollapse: 'collapse' }}>
                            <thead className="table-light">
                              <tr>
                                <th style={{ width: '50%' }}>Item Description</th>
                                <th style={{ width: '10%', textAlign: 'center' }}>Quantity</th>
                                <th style={{ width: '10%', textAlign: 'center' }}>Unit</th>
                                <th style={{ width: '15%', textAlign: 'right' }}>Price (Php)</th>
                                <th style={{ width: '15%', textAlign: 'right' }}>Total</th>
                              </tr>
                            </thead>
                            <tbody>
                              {rows.map((r, idx) => (
                                <tr key={`fpv-${idx}`}>
                                  <td>{r.description || ''}</td>
                                  <td className="text-center">{r.quantity || ''}</td>
                                  <td className="text-center">{r.unit || ''}</td>
                                  <td className="text-end">{currency(r.price)}</td>
                                  <td className="text-end">{currency(r.total)}</td>
                                </tr>
                              ))}
                              <tr className="table-light">
                                <td colSpan={4} className="text-end fw-bold">TOTAL</td>
                                <td className="text-end fw-bold">{currency(grand)}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      );
                    })()}
                  </div>
                </div>

                {/* Monitoring & Evaluation after narrative sections */}
                <div className="mb-3">
                  <strong>Monitoring and Evaluation Plan:</strong>
                  <div className="mt-2">
                    {renderMonitoringEvaluationTable(project.monitoring_evaluation || project.monitoring_evaluation_plan)}
                  </div>
                </div>

                {/* Sustainability Plan last */}
                <div className="mb-3">
                  <strong>Sustainability Plan:</strong>
                  <div className="mt-1" style={{ whiteSpace: 'pre-wrap' }}>{project.sustainability_plan || '—'}</div>
                </div>
              </Card.Body>
            </Collapse>
          </Card>
        )}

        {/* Decision Modal */}
        <Modal show={showDecisionModal} onHide={() => setShowDecisionModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>
              {decision === 'Approved' ? 'Approve' : 'Reject'} Evaluation Report
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {decision === 'Approved' ? (
              <>
                <div className="text-center">
                  <i className="bi bi-check-circle-fill fs-1 text-success mb-3"></i>
                  <h5>Are you sure you want to approve this evaluation report?</h5>
                  <p className="text-muted">
                    This action will move the report to the next approval stage.
                  </p>
                </div>
                <Form.Group className="mt-3">
                  <Form.Label>Remarks (optional)</Form.Label>
                  <Form.Control 
                    as="textarea" 
                    rows={3} 
                    value={remarks} 
                    onChange={(e) => setRemarks(e.target.value)} 
                    placeholder="Add any additional comments or notes..."
                  />
                </Form.Group>
              </>
            ) : (
              <>
                <div className="text-center">
                  <i className="bi bi-x-circle-fill fs-1 text-danger mb-3"></i>
                  <h5>Are you sure you want to reject this evaluation report?</h5>
                  <p className="text-muted">
                    This action will reject the evaluation report.
                  </p>
                </div>
                <Form.Group className="mt-3">
                  <Form.Label>Remarks (optional)</Form.Label>
                  <Form.Control 
                    as="textarea" 
                    rows={3} 
                    value={remarks} 
                    onChange={(e) => setRemarks(e.target.value)} 
                    placeholder="Please provide a reason for rejection..."
                  />
                </Form.Group>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button 
              variant="secondary" 
              onClick={() => setShowDecisionModal(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button 
              variant={decision === 'Approved' ? 'success' : 'danger'}
              onClick={submitDecision}
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Processing...
                </>
              ) : (
                <>
                  <i className={`bi bi-${decision === 'Approved' ? 'check' : 'x'}-lg me-2`}></i>
                  {decision === 'Approved' ? 'Approve' : 'Reject'} Report
                </>
              )}
            </Button>
          </Modal.Footer>
        </Modal>

      {/* Success Modal */}
      <Modal show={showSuccessModal} onHide={() => setShowSuccessModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Success</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center py-3">
            <h5 className="mb-0">{successMessage}</h5>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="success" onClick={() => setShowSuccessModal(false)}>
            OK
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Evaluation Report Modal */}
      <EvaluationReportModal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        reportId={id}
        onSuccess={() => {
          // Reload report data after successful edit
          const fetchReport = async () => {
            try {
              const response = await axios.get(`/reports/${id}`);
              setReport(response.data);
              showSuccess('Evaluation report updated successfully!');
            } catch (err) {
              console.error('Failed to reload report', err);
            }
          };
          fetchReport();
          setShowEditModal(false);
        }}
      />
    </div>
  );
};

export default ReportView;


